import { useMemo, useState } from 'preact/hooks';
import { lineAmount, stockRow, type Paisa } from '@khata/core';
import { DateInput, Field, MoneyInput, QtyInput, TextInput } from '../components/index.js';
import { Picker } from '../components/Picker.js';
import { AccountPicker } from '../components/AccountPicker.js';
import { fromApi, isErr, money, quantity, useFields, useSave } from '../lib/form.js';
import { num, qty as fmtQty, taka } from '../lib/format.js';
import { count, t } from '../lib/i18n.js';
import { master, state, submit, today } from '../lib/khata.js';
import { FormErrorLine, SaveButton, Sheet } from './Sheet.js';

interface Line {
  itemId: string;
  qty: string;
  unit: string;
}

export function TxSheet(props: { kind: 'sale' | 'purchase'; partyId?: string }) {
  const m = master.value;
  const s = state.value;
  const isSale = props.kind === 'sale';
  const parties = [...m.parties.values()].filter(
    (p) => p.active && p.type === (isSale ? 'customer' : 'vendor'),
  );
  const accounts = [...m.accounts.values()].filter((a) => a.active);
  const [f, set] = useFields({
    partyId: props.partyId ?? '',
    q: '',
    paid: '',
    accountId: accounts[0]?.id ?? '',
    date: today.value,
    note: '',
  });
  const [lines, setLines] = useState<Line[]>([]);
  const [error, busy, save] = useSave();

  const pick = useMemo(() => {
    const ql = f.q.trim().toLowerCase();
    if (!ql) return [];
    return [...m.items.values()]
      .filter(
        (i) =>
          i.active &&
          !lines.some((l) => l.itemId === i.id) &&
          `${i.name} ${i.category} ${i.origin}`.toLowerCase().includes(ql),
      )
      .slice(0, 6)
      .map((i) => ({ item: i, row: stockRow(s, i) }));
  }, [f.q, m, s, lines]);

  const add = (id: string) => {
    const item = m.items.get(id);
    if (!item) return;
    const row = stockRow(s, item);
    const unit = isSale ? item.sellPrice : row.avgCost;
    setLines([...lines, { itemId: id, qty: '1', unit: unit ? String(unit / 100) : '' }]);
    set('q', '');
  };
  const update = (i: number, patch: Partial<Line>) =>
    setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const remove = (i: number) => setLines(lines.filter((_, j) => j !== i));

  let total = 0 as Paisa;
  const amounts = lines.map((l) => {
    const q = quantity(l.qty, 'qty');
    const u = money(l.unit, 'unit', { allowEmpty: true });
    if (isErr(q) || isErr(u)) return null;
    const a = lineAmount(q, u);
    total = (total + a) as Paisa;
    return a;
  });
  const walkIn = f.partyId === '';
  const paidParsed = walkIn ? total : money(f.paid, 'paid', { allowEmpty: true });
  const due = isErr(paidParsed) ? 0 : Math.max(0, total - paidParsed);

  const onSave = () =>
    save(async () => {
      if (lines.length === 0) return { message: t('sheet_addOne') };
      const built = [];
      for (let i = 0; i < lines.length; i++) {
        const l = lines[i]!;
        const q = quantity(l.qty, `lines.${i}`);
        if (isErr(q))
          return {
            message: `${m.items.get(l.itemId)?.name ?? t('item')}: ${q.message}`,
            field: q.field,
          };
        const u = money(l.unit, `lines.${i}`, { allowEmpty: true });
        if (isErr(u))
          return {
            message: `${m.items.get(l.itemId)?.name ?? t('item')}: ${u.message}`,
            field: u.field,
          };
        built.push(
          isSale
            ? { itemId: l.itemId, qty: q, unitPrice: u }
            : { itemId: l.itemId, qty: q, unitCost: u },
        );
      }
      const paid = walkIn ? total : money(f.paid, 'paid', { allowEmpty: true });
      if (isErr(paid)) return paid;
      const r = await submit(props.kind, {
        date: f.date,
        partyId: walkIn ? null : f.partyId,
        lines: built,
        paid,
        accountId: f.accountId,
        note: f.note,
      });
      if (!r.ok) return fromApi(r.error, props.kind);
      const e = r.entry as { no: number; total: Paisa };
      return t(isSale ? 'sheet_saleSaved' : 'sheet_purchaseSaved', {
        no: num(e.no),
        total: taka(e.total),
      });
    });

  return (
    <Sheet
      title={isSale ? t('sheet_newSale') : t('sheet_newPurchase')}
      footer={
        <SaveButton
          label={`${t(isSale ? 'sheet_saveSale' : 'sheet_savePurchase')} · ${taka(total)}`}
          busy={busy}
          onClick={onSave}
        />
      }
    >
      <Field label={isSale ? t('customer') : t('vendor')}>
        <Picker
          value={f.partyId}
          onChange={(v) => set('partyId', v)}
          placeholder={isSale ? t('picker_customer') : t('picker_vendor')}
          noneLabel={isSale ? t('sheet_walkIn') : t('sheet_cashPurchase')}
          invalid={error?.field === 'partyId'}
          testId="party-picker"
          options={parties.map((p) => ({ id: p.id, label: p.name, sub: p.phone || p.note }))}
        />
      </Field>
      <Field label={t('sheet_addItem')}>
        <TextInput
          value={f.q}
          onInput={(v) => set('q', v)}
          placeholder={t('sheet_searchItems')}
          autoFocus={!props.partyId}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && pick[0]) {
              e.preventDefault();
              add(pick[0].item.id);
            }
          }}
        />
        {pick.length > 0 && (
          <div class="pick">
            {pick.map(({ item, row }) => (
              <button key={item.id} type="button" onClick={() => add(item.id)}>
                <span>
                  {item.name}{' '}
                  <span class="sub">
                    {item.origin ? item.origin + ' · ' : ''}
                    {fmtQty(row.qty)} {item.unit} {t('sheet_left')}
                  </span>
                </span>
                <span>{taka(isSale ? item.sellPrice : row.avgCost)}</span>
              </button>
            ))}
          </div>
        )}
        {f.q.trim() !== '' && pick.length === 0 && <div class="help">{t('sheet_noItemMatch')}</div>}
      </Field>
      {lines.map((l, i) => {
        const item = m.items.get(l.itemId);
        const a = amounts[i];
        return (
          <div key={l.itemId} class="line">
            <div>
              <div class="name">{item?.name ?? t('label_deletedItem')}</div>
              <div class="sub">
                {a == null ? '…' : taka(a)} · {item?.unit ?? ''}
              </div>
            </div>
            <QtyInput
              value={l.qty}
              onInput={(v) => update(i, { qty: v })}
              aria-label={t('sheet_qty')}
              data-role="qty"
              invalid={error?.field === `lines.${i}`}
            />
            <MoneyInput
              value={l.unit}
              onInput={(v) => update(i, { unit: v })}
              aria-label={isSale ? t('sheet_price') : t('sheet_cost')}
              data-role="unit"
            />
            <button
              type="button"
              class="btn btn-icon btn-ghost"
              aria-label={t('sheet_remove')}
              onClick={() => remove(i)}
            >
              ✕
            </button>
          </div>
        );
      })}
      <div class="total-line">
        <span class="muted">
          {t('sheet_total')} · {count(lines.length, 'item', 'items')}
        </span>
        <strong>{taka(total)}</strong>
      </div>
      <div class="grid-2">
        <Field label={t('sheet_paidNow')}>
          {walkIn ? (
            <div class="input" style={{ display: 'flex', alignItems: 'center' }}>
              {taka(total)} · {t('sheet_inFull')}
            </div>
          ) : (
            <MoneyInput
              value={f.paid}
              onInput={(v) => set('paid', v)}
              invalid={error?.field === 'paid'}
            />
          )}
        </Field>
        <Field label={isSale ? t('sheet_into') : t('sheet_from')}>
          <AccountPicker
            value={f.accountId}
            onChange={(v) => set('accountId', v)}
            accounts={accounts}
            testId="account-picker"
          />
        </Field>
      </div>
      {!walkIn && (
        <div class="total-line" style={{ borderTop: 0, paddingTop: 0 }}>
          <span class="muted">
            {t('sheet_remaining')} ({isSale ? t('bokeya') : t('parties_youOwe')})
          </span>
          <strong class={due > 0 ? 'bad' : 'muted'}>{taka(due as Paisa)}</strong>
        </div>
      )}
      <div class="grid-2">
        <Field label={t('date')}>
          <DateInput
            value={f.date}
            onInput={(v) => set('date', v)}
            invalid={error?.field === 'date'}
          />
        </Field>
        <Field label={t('note')}>
          <TextInput value={f.note} onInput={(v) => set('note', v)} placeholder={t('optional')} />
        </Field>
      </div>
      <FormErrorLine error={error} />
    </Sheet>
  );
}
